import * as ChannelsModel from "./channels-models";
import { OutgoingQosEvent, TelemetryService } from "../../../ShellCommon/logger/telemetry-service";
import { AgentProfileEntity } from "../entity-service-implementation/agent-profile-entity-model";
import { AgentProfileEntityService } from "../entity-service-implementation/agent-profile-entity-service";
import { LoggingService } from "../loggingSvc";
import { ServiceDeskConfig } from "../../Startup/serviceDeskConfig";
import { MsalAuthenticationServiceImplementation } from ".././msal/msal-authentication-service";
import { ChannelsConstants } from "./channels-constants";

/** Interface to call into Channels Agent SDK lib */
export interface ChannelsServiceApi {
  /** Initialize the SDK for logged in agent */
  /** @param modalities supported modalities */
  GetSdk(modalities?: string[]): ng.IPromise<ChannelsModel.PromiseResult>;

  /**
   * method to log error
   * @param errorMessage error message
   * @param userMessage user message to show in the UI
   * @param errorDetails error details to log.
   */
  logError(errorMessage: string, userMessage?: string, errorDetails?: {}): void;

  /**
   * method to log information message
   * @param infoMessage information message
   * @param infoDetails information data
   */
  logInfo(infoMessage: string, infoDetails?: {}): void;

  logOutgoingOperation(config: OutgoingQosEvent, start: number, succeeded: boolean, responseContent?: any): void;
}

const agentSdkRequestMaxRetry = 3;
const agentSdkRequestTimeout = 60 * 1000;

/**
 * Channels SDKservice
 */
export class ChannelsService implements ChannelsServiceApi {
  private channelsSdk: ChannelsModel.ChannelsSdk;
  private sdkContext: ChannelsModel.ChannelsContext;
  private agentProfile: AgentProfileEntity;
  private deferredGetSdk: ng.IDeferred<ChannelsModel.PromiseResult>;

  // @ngInject
  constructor(
    private $q: ng.IQService,
    private loggingService: LoggingService,
    private telemetryService: TelemetryService,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation,
    private agentProfileEntityService: AgentProfileEntityService,
    private sdConfig: ServiceDeskConfig,
    private channelsConstants: ChannelsConstants
  ) {
    this.agentProfile = this.agentProfileEntityService.getAgentProfileSync();
  }

  /**
   * method to initialize channels SDK
   * @param modalities list of modalities supported by web sdk
   */
  public GetSdk(modalities?: string[]): ng.IPromise<ChannelsModel.PromiseResult> {
    if (!this.agentProfile) {
      return this.$q.reject({ result: this.channelsConstants.promiseStatus.AgentProfileInitiliazeFailed });
    }
    if (this.channelsSdk != undefined) {
      return this.$q.resolve({
        data: this.channelsSdk,
        result: this.channelsConstants.promiseStatus.AgentSdkInitializeOk
      });
    }

    if (!modalities) {
      modalities = [this.channelsConstants.webFormModality];
    }

    if (!this.deferredGetSdk) {
      this.deferredGetSdk = this.$q.defer<ChannelsModel.PromiseResult>();
      this.sdkContext = {
        agentId: this.agentProfile.id,
        authService: this.msalAuthenticationService,
        upn: this.agentProfile.aadUpn,
        requestMaxRetry: agentSdkRequestMaxRetry,
        requestTimeout: agentSdkRequestTimeout,
        siteId: this.agentProfile.siteId,
        supportedModalities: modalities
      };

      // callback function to assign SDK instance
      let initAgentSDKCallbackFunc = (sdk: ChannelsModel.ChannelsSdk) => {
        this.channelsSdk = sdk;
        this.logInfo("Agent Channels SDK initialization successful.");
        return this.deferredGetSdk.resolve({
          data: this.channelsSdk,
          result: this.channelsConstants.promiseStatus.AgentSdkInitializeOk
        });
      };

      // callback function to capture error
      let errorCallbackFunc = (error: any) => {
        this.logError("Failed to Initialize Agent Channels SDK");
        return this.deferredGetSdk.reject({
          result: this.channelsConstants.promiseStatus.AgentSdkInitializeFailed,
          error: error
        });
      };

      if (this.sdConfig.currentEnvironment && this.sdConfig.currentEnvironment != "prod") {
        (<any>window).channelServicesInt.initAgentSdk(this.sdkContext, initAgentSDKCallbackFunc, errorCallbackFunc);
      } else {
        (<any>window).channelServices.initAgentSdk(this.sdkContext, initAgentSDKCallbackFunc, errorCallbackFunc);
      }
    }
    return this.deferredGetSdk.promise;
  }

  /**
   * method to log error
   * @param errorMessage error message to log.
   * @param userMessage user visible message to log
   * @param errorDetails addition error details to log
   */
  public logError(errorMessage: string, userMessage?: string, errorDetails?: {}): void {
    let errorObj = {
      data: {
        message: errorMessage,
        agentId: this.agentProfile ? this.agentProfile.email : "Agent profile not available.",
        data: errorDetails
      }
    };

    this.loggingService.logError(errorObj);
  }

  /**
   * method to log information
   * @param infoMessage information message to log
   * @param infoDetails additiona information details to log
   */
  public logInfo(infoMessage: string, infoDetails?: any): void {
    let infoObj = {
      data: {
        message: infoMessage,
        agentId: this.agentProfile ? this.agentProfile.email : "Agent profile not available.",
        data: infoDetails
      }
    };

    this.loggingService.logInfo(infoObj);
  }

  /**
   * method  to log out going oprerations
   * @param config OutgoingQosEvent
   * @param start  starting time
   * @param succeeded  rather the opreration is successded or not
   * @param responseContent optional responseContent from the api
   */
  public logOutgoingOperation(
    config: OutgoingQosEvent,
    start: number,
    succeeded: boolean,
    responseContent?: any
  ): void {
    config.cV = this.telemetryService.getCorrelationVector();
    config.isSuccess = succeeded;
    config.latencyMs = window.performance.now() - start;
    config.responseContent = responseContent;
    config.responseStatusCode =
      responseContent && responseContent.context && responseContent.context.statusCode
        ? responseContent.context.statusCode
        : "0";
    this.telemetryService.pushOutgoingQosEvent(config);
  }

  private subscribeAgentProfile(): void {
    this.agentProfileEntityService.GetAgentProfile(false).subscribe(
      (agentProfile: AgentProfileEntity) => {
        this.agentProfile = agentProfile;
      },
      (error) => {
        this.logError("Availability service initialization failed, unable to retrieve agent profile, error = " + error);
      }
    );
  }
}
